import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  WorkCategoriesV2Service,
  WorkCategory,
  WorkContactAndProjectsResultDto,
  WorkEquipment,
  WorkItem,
  WorkMegasearchResultDto,
  WorkStatus,
  WorkStatusSelectorEnum,
  WorkStatusV2Service,
  WorkStocklocation,
  WorkUser,
} from '@work-frontend-monorepo/work-rest-api';
import { lastValueFrom } from 'rxjs';
import { UserEmploymentHintPipe } from '../../pipes/user-employment-hint.pipe';
import {
  GlobalMegasearchResultContactAndProjectsComponent,
  MegasearchResultClickedInterface,
} from '../global-megasearch-result-contact-and-projects/global-megasearch-result-contact-and-projects.component';
import { TranslateModule } from '@ngx-translate/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { NgTemplateOutlet } from '@angular/common';
import { AppRoutePaths, AppRouteViewTypes } from '../../../app-route.enums';
import {
  GetEquipmenttypeIconNamePipe,
  GetItemProductimagePathFromDocuments,
  HighlightPipe,
  JoinByPipe,
  PrependServerToFileSrcPipe,
} from '@work-frontend-monorepo/work-ui/pipes';
import { InfoblockComponent } from '@work-frontend-monorepo/work-ui/data-display';
import { CheckboxComponent } from '@work-frontend-monorepo/work-ui/forms';
import { IconComponent } from '@work-frontend-monorepo/work-ui/layout';
import { AccordionItemComponent } from '@work-frontend-monorepo/work-ui/wizard';

export type MegaSearchResultAlignment = 'start' | 'center' | 'end';

@Component({
  selector: 'work-office-global-megasearch-results',
  templateUrl: './global-megasearch-results.component.html',
  styleUrls: ['./global-megasearch-results.component.scss'],
  standalone: true,
  imports: [
    InfoblockComponent,
    TranslateModule,
    PrependServerToFileSrcPipe,
    HighlightPipe,
    GetEquipmenttypeIconNamePipe,
    JoinByPipe,
    NgTemplateOutlet,
    GlobalMegasearchResultContactAndProjectsComponent,
    GetItemProductimagePathFromDocuments,
    CheckboxComponent,
    IconComponent,
    AccordionItemComponent,
    UserEmploymentHintPipe,
  ],
  animations: [
    trigger('overlayAnimation', [
      // Entry animation (slide in from top with fade)
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(-6px)',
        }),
        animate(
          '100ms ease-out',
          style({
            opacity: 1,
            transform: 'translateY(0)',
          }),
        ),
      ]),
      // Exit animation (slide out to top with fade)
      transition(':leave', [
        style({
          opacity: 1,
          transform: 'translateY(0)',
        }),
        animate(
          '100ms ease-in',
          style({
            opacity: 0,
            transform: 'translateY(-6px)',
          }),
        ),
      ]),
    ]),
  ],
})
export class GlobalMegasearchResultsComponent implements OnInit, OnChanges, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly categoriesV2Service = inject(WorkCategoriesV2Service);
  private readonly statusV2Service = inject(WorkStatusV2Service);

  @ViewChild('overlayContent') overlayContentTemplate!: TemplateRef<any>;

  @Input({ required: true }) WorkMegasearchResultDto: WorkMegasearchResultDto | undefined;
  @Input({ required: true }) searchterm!: string;
  @Input() isVisible = false;
  @Input() showAsOverlay = true;
  @Input() alignment: MegaSearchResultAlignment = 'start';
  @Input() renderUserAsLink = false;
  @Input() renderContactAsLink = false;
  @Input() renderProjectAsLink = false;
  @Input() renderEquipmentAsLink = false;
  @Input() renderStocklocationAsLink = false;
  @Input() renderItemAsLink = false;
  public containerTypeIdFilters = input<number[]>([]);
  public disableResultContactLinks = input(false);
  public disableResultProjectLinks = input(false);
  public enableContactAndProjectsFilter = input(false);

  @Output() isVisibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  public contactClicked = output<MegasearchResultClickedInterface>();
  public projectClicked = output<MegasearchResultClickedInterface>();
  public userClicked = output<WorkUser>();
  public equipmentClicked = output<WorkEquipment>();
  public stocklocationClicked = output<WorkStocklocation>();
  public itemClicked = output<WorkItem>();

  protected readonly contactCategories = signal<WorkCategory[]>([]);
  protected readonly projectStatuses = signal<WorkStatus[]>([]);
  protected readonly contactCategoryIdFilters = signal<number[]>([]);
  protected readonly projectStatusIdFilters = signal<number[]>([]);
  private readonly megasearchResult = signal<WorkMegasearchResultDto | undefined>(undefined);

  protected readonly rawContactAndProjects = computed<WorkContactAndProjectsResultDto[]>(
    () => this.megasearchResult()?.contactAndProjects ?? [],
  );

  /** Contact/project results after applying the (optional) contact-category and project-status filters. */
  protected readonly displayedContactAndProjects = computed<WorkContactAndProjectsResultDto[]>(() => {
    const selectedCategoryIds = this.contactCategoryIdFilters();
    const selectedStatusIds = this.projectStatusIdFilters();
    // Archivierte Projekte (active === false) generell ausblenden.
    let contactAndProjectsList = this.rawContactAndProjects().map((contactAndProjects) => ({
      ...contactAndProjects,
      projects: contactAndProjects.projects.filter((project) => project.active !== false),
    }));
    if (selectedCategoryIds.length > 0) {
      contactAndProjectsList = contactAndProjectsList.filter(
        (contactAndProjects) =>
          contactAndProjects.contact.categoryId !== null && selectedCategoryIds.includes(contactAndProjects.contact.categoryId),
      );
    }
    if (selectedStatusIds.length > 0) {
      contactAndProjectsList = contactAndProjectsList
        .map((contactAndProjects) => ({
          ...contactAndProjects,
          projects: contactAndProjects.projects.filter(
            (project) => project.status != null && selectedStatusIds.includes(project.status.id),
          ),
        }))
        .filter((contactAndProjects) => contactAndProjects.projects.length > 0);
    }
    return contactAndProjectsList;
  });

  protected readonly hasNoResults = computed<boolean>(() => {
    const result = this.megasearchResult();
    if (!result) {
      return false;
    }
    return (
      this.rawContactAndProjects().length === 0 &&
      !result.items?.length &&
      !result.equipments?.length &&
      !result.stocklocations?.length &&
      !result.users?.length
    );
  });

  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal | null = null;

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.toggleResults(false);
    }
  }

  @HostListener('click', ['$event'])
  onComponentClick(event: MouseEvent) {
    // Handle component click, prevent it from propagating to document
    event.stopPropagation();
  }

  ngOnInit() {
    // Add a click listener to the document
    document.addEventListener('click', this.handleDocumentClick.bind(this), true);
    // this.setupParentClickListener();
    if (this.enableContactAndProjectsFilter()) {
      void this.loadContactCategories();
      void this.loadProjectStatuses();
    }
  }

  ngOnDestroy() {
    // Remove the click listener when the component is destroyed
    document.removeEventListener('click', this.handleDocumentClick.bind(this), true);

    // Dispose of the overlay if it exists
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const megasearchResultChanges = changes['WorkMegasearchResultDto'];
    if (
      megasearchResultChanges &&
      megasearchResultChanges.currentValue &&
      megasearchResultChanges.currentValue !== megasearchResultChanges.previousValue
    ) {
      this.toggleResults(true);
      this.checkContainerTypeIdFilters();
      this.megasearchResult.set(this.WorkMegasearchResultDto);
    }
    if (changes['isVisible'] && changes['isVisible'].currentValue !== changes['isVisible'].previousValue) {
      if (this.isVisible) {
        this.showOverlay();
      } else {
        this.hideOverlay();
      }
    }
  }

  public toggleResults(visible: boolean) {
    this.isVisible = visible;
    this.isVisibleChange.emit(this.isVisible);

    if (this.isVisible) {
      this.showOverlay();
    } else {
      this.hideOverlay();
    }
  }

  private handleDocumentClick(event: MouseEvent) {
    // Check if the click is outside the component and its parent
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      const overlayElement = this.overlayRef.overlayElement;
      const parentElement = this.elementRef.nativeElement.parentElement;

      // Check if clicked inside overlay or parent component
      const clickedInOverlay = overlayElement.contains(event.target as Node);
      const clickedInParent = parentElement && parentElement.contains(event.target as Node);

      // Only close if clicked outside both the overlay and parent
      if (!clickedInOverlay && !clickedInParent) {
        this.toggleResults(false);
      }
    }
  }

  private createOverlay() {
    if (!this.overlayRef) {
      const positionStrategy = this.getPositionStrategy();
      const config = new OverlayConfig({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        hasBackdrop: false,
        width: this.getParentWidth(),
      });
      this.overlayRef = this.overlay.create(config);
    }
  }

  private getParentWidth(): number {
    const parentElement = this.elementRef.nativeElement.parentElement;
    return parentElement ? Math.max(parentElement.getBoundingClientRect().width, 300) : 300;
  }

  private getPositionStrategy(): FlexibleConnectedPositionStrategy {
    // Get the reference element for positioning (typically the parent input field)
    const parentElement = this.elementRef.nativeElement.parentElement;

    // Define positions for the overlay
    const positions: ConnectedPosition[] = [];

    // Position below the target element, aligned based on the alignLeft input
    positions.push({
      originX: this.alignment,
      originY: 'bottom',
      overlayX: this.alignment,
      overlayY: 'top',
      offsetY: 12,
    });

    // Fallback position above the target
    positions.push({
      originX: this.alignment,
      originY: 'top',
      overlayX: this.alignment,
      overlayY: 'bottom',
      offsetY: -12,
    });

    // Create a flexible connected position strategy
    return this.overlay
      .position()
      .flexibleConnectedTo(parentElement)
      .withPositions(positions)
      .withPush(true) // Allows the overlay to be pushed into view if it would otherwise go off-screen
      .withViewportMargin(16); // Keep 16px away from the edge of the viewport
  }

  private showOverlay() {
    this.createOverlay();

    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      // Create the portal if it doesn't exist
      if (!this.portal && this.overlayContentTemplate) {
        this.portal = new TemplatePortal(this.overlayContentTemplate, this.viewContainerRef);
      }

      // Attach the portal to the overlay
      if (this.portal) {
        this.overlayRef.attach(this.portal);

        // Make sure to update the position
        if (this.overlayRef) {
          (this.overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy).apply();
        }
      }
    }
  }

  private hideOverlay() {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  private checkContainerTypeIdFilters() {
    if (this.containerTypeIdFilters().length > 0 && this.WorkMegasearchResultDto) {
      this.WorkMegasearchResultDto.items = this.WorkMegasearchResultDto?.items.filter(
        (item) => !item.entity.containertypeId || this.containerTypeIdFilters().includes(item.entity.containertypeId),
      );
    }
  }

  protected updateContactCategoryIdFilter(categoryId: number, checked: boolean) {
    this.contactCategoryIdFilters.update((categoryIds) =>
      checked ? [...categoryIds, categoryId] : categoryIds.filter((id) => id !== categoryId),
    );
  }

  protected updateProjectStatusIdFilter(statusId: number, checked: boolean) {
    this.projectStatusIdFilters.update((statusIds) => (checked ? [...statusIds, statusId] : statusIds.filter((id) => id !== statusId)));
  }

  private async loadContactCategories() {
    try {
      this.contactCategories.set(await lastValueFrom(this.categoriesV2Service.categoriesV2ControllerGetAll()));
    } catch {
      // Filter ist eine optionale Erweiterung — bei fehlender Berechtigung o.ä. still ausblenden.
      this.contactCategories.set([]);
    }
  }

  private async loadProjectStatuses() {
    try {
      this.projectStatuses.set(
        await lastValueFrom(this.statusV2Service.statusV2ControllerGetAllBySelector({ selector: WorkStatusSelectorEnum.Projects })),
      );
    } catch {
      // Filter ist eine optionale Erweiterung — bei fehlender Berechtigung o.ä. still ausblenden.
      this.projectStatuses.set([]);
    }
  }

  public focusFirstResult(): void {
    const container = this.overlayRef?.overlayElement || this.elementRef.nativeElement;
    const firstFocusable = container.querySelector('[tabindex="0"], a[tabindex]') as HTMLElement;
    firstFocusable?.focus();
  }

  protected readonly AppRouteViewTypes = AppRouteViewTypes;
  protected readonly AppRoutePaths = AppRoutePaths;
}
